import { useEffect, useState, type ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { supabaseImageUrl } from "@/lib/image-utils";

// Supabase Storage answers 429 "SlowDown" when too many file requests arrive
// at once, which shows up as a broken-image "?". Load lazily so only on-screen
// rows fetch, and retry a failed load once after a jittered delay so retries
// don't burst together again. If the retry also fails, the broken image is
// left showing on purpose -- it flags a real problem instead of hiding it.
const RETRY_MIN_MS = 1000;
const RETRY_JITTER_MS = 2000;

interface ThumbnailProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  url: string | null | undefined;
  size: number;             // square transform size in px (e.g. 72, 300)
  className?: string;       // applied to both the image and the no-image placeholder
}

export function Thumbnail({ url, size, className, alt = "", ...imgProps }: ThumbnailProps) {
  const [retried, setRetried] = useState(false);

  useEffect(() => {
    setRetried(false);
  }, [url]);

  const base = supabaseImageUrl(url, { width: size, height: size });

  if (!base) {
    return <div className={cn("rounded border border-dashed border-border bg-muted/50", className)} />;
  }

  // A distinct query param on the retry makes the browser actually re-request
  // instead of reusing the failed response.
  const src = retried ? `${base}${base.includes("?") ? "&" : "?"}retry=1` : base;

  return (
    <img
      {...imgProps}
      key={src}
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      onError={() => {
        if (!retried) {
          setTimeout(() => setRetried(true), RETRY_MIN_MS + Math.random() * RETRY_JITTER_MS);
        }
      }}
    />
  );
}
